import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { IsActiveMatchOptions, Params, RouterLink, RouterLinkActive } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuItem } from '@angular/material/menu';

import { CmsButton } from '@shared/models/cms.model';
import { CmsImage } from '@shared/ui/cms-image/cms-image';
import { LanguageService } from '@core/i18n/language.service';

/**
 * Renders a CMS `button.button` component as a Material button.
 *
 * Internal links (`isExternalLink === false`) use `routerLink`, prefixed with the active language so
 * they resolve under the localized `/:lang` routes — the modern replacement for the legacy
 * `| localize` pipe. External links use a plain `href`.
 */
@Component({
    selector: 'cms-btn',
    imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, MatButtonModule, MatMenuItem, CmsImage],
    templateUrl: './cms-btn.html',
    styleUrl: './cms-btn.scss',
    standalone: true,
    host: {
        '[class.cms-btn--menu-item]': 'menuItem()',
    },
})
export class CmsBtn {
    private readonly _language: LanguageService = inject(LanguageService);

    /** The CMS button configuration to render. */
    public readonly config: InputSignal<CmsButton> = input.required<CmsButton>();

    /** When true, renders the anchor with `mat-menu-item` so it integrates into a `mat-menu`. */
    public readonly menuItem: InputSignal<boolean> = input<boolean>(false);

    /** A `link`-style button renders as a plain text link rather than a Material button. */
    protected readonly isLink: Signal<boolean> = computed((): boolean => this.config().style === 'link');

    /**
     * Anchor `target` as a valid HTML keyword. The CMS enum is `_blank`/`_self`, but Strapi's GraphQL
     * strips the leading underscore (`blank`/`self`) — binding that raw value makes `target="self"` a
     * *named* browsing context, which opens a new tab. Restore the `_` so internal links stay put.
     */
    protected readonly target: Signal<string> = computed((): string =>
        this.config().target === 'blank' ? '_blank' : '_self',
    );

    /** Internal link as a language-prefixed router-link command array (e.g. `/en/about`). */
    protected readonly routerLink: Signal<ReadonlyArray<string>> = computed((): ReadonlyArray<string> =>
        this._language.localizedCommands(this.config().link),
    );

    /** CMS `queryParams` is free-form JSON; surface it as router `Params`. */
    protected readonly queryParams: Signal<Params> = computed(
        (): Params => (this.config().queryParams as Params) ?? {},
    );

    /**
     * Fallback accessible name used only when the visible label is empty AND no button icon is
     * present. In that case the link has no discernible text, so we surface the raw href to
     * satisfy WCAG 2.4.4 / 4.1.2. CMS editors should always set a label — this is a safety net.
     */
    protected readonly ariaLabel: Signal<string | null> = computed((): string | null => {
        const btn: CmsButton = this.config();

        return !btn.label && !btn.buttonIcon?.url ? (btn.link ?? null) : null;
    });

    /**
     * `routerLinkActive` matching for internal links: the path must match exactly (so a page link is
     * not marked active on sibling/child routes, and a home link is not active everywhere), while
     * query params and fragment are ignored. Switch `paths` to `'subset'` for prefix (section) matching.
     */
    protected readonly activeMatchOptions: IsActiveMatchOptions = {
        paths: 'exact',
        queryParams: 'ignored',
        matrixParams: 'ignored',
        fragment: 'ignored',
    };
}
